import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, X, Loader2, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { useAssistant } from "@/contexts/AssistantContext";
import { useAuth } from "@/contexts/AuthContext";
import { ScholarAvatar } from "@/components/assistant/ScholarAvatar";
import { AssistantMessage } from "@/components/assistant/AssistantMessage";
import { EscalateBox } from "@/components/assistant/EscalateBox";

function panelStyle(anchor) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { rect } = anchor;
    const width = Math.min(vw - 16, 400);
    const height = Math.min(600, vh - 16, Math.max(320, anchor.bottom ? rect.top - 20 : vh - rect.bottom - 20));
    const st = { position: "fixed", zIndex: 61, width, height };
    if (anchor.right) st.right = Math.max(8, Math.min(vw - rect.right, vw - width - 8));
    else st.left = Math.max(8, Math.min(rect.left, vw - width - 8));
    const top = anchor.bottom ? rect.top - 12 - height : rect.bottom + 12;
    st.top = Math.max(8, Math.min(top, vh - height - 8));
    return st;
}

export function AssistantPanel({ onClose, anchor }) {
    const { course, teacherIntent, prefill, setPrefill, activity } = useAssistant();
    const { user } = useAuth();
    const isAdmin = ["super_admin", "shop_admin"].includes(user?.role);
    const [messages, setMessages] = useState([]);
    const [convId, setConvId] = useState(null);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [suggestTeacher, setSuggestTeacher] = useState(false);
    const scrollRef = useRef(null);
    const ctxKey = course ? `${course.type}:${course.id}` : "general";

    useEffect(() => { setMessages([]); setConvId(null); setError(""); setSuggestTeacher(false); }, [ctxKey]);
    useEffect(() => { if (prefill) { setInput(prefill); setPrefill(""); } }, [prefill, setPrefill]);
    useEffect(() => { scrollRef.current?.scrollTo({ top: 1e6, behavior: "smooth" }); }, [messages, busy]);

    const ask = async (e) => {
        e.preventDefault();
        const q = input.trim();
        if (!q || busy) return;
        setInput(""); setError(""); setBusy(true);
        setMessages((m) => [...m, { role: "user", text: q }]);
        try {
            const body = { question: q, conversation_id: convId, activity: course && activity ? activity : null };
            if (!convId) body.context = course ? { type: course.type, id: course.id } : { type: "general" };
            const { data } = await api.post("/assistant/ask", body);
            setConvId(data.conversation_id);
            setSuggestTeacher(!!data.suggest_teacher);
            setMessages((m) => [...m, { role: "assistant", text: data.answer, cached: data.cached, source: data.source }]);
        } catch (err) {
            setError(err?.response?.data?.detail || "L'assistant n'a pas pu répondre.");
        }
        setBusy(false);
    };

    const answered = messages.some((m) => m.role === "assistant");

    return (
        <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.22 }}
            className="rounded-2xl flex flex-col overflow-hidden"
            style={{ ...panelStyle(anchor), background: "rgba(11,15,26,0.96)", border: "1px solid var(--border-dark)", backdropFilter: "blur(18px)", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}
            data-testid="assistant-panel"
        >
            <div className="flex items-center gap-3 px-4 py-3.5" style={{ borderBottom: "1px solid var(--border-dark)" }}>
                <ScholarAvatar size={42} />
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight" style={{ color: "var(--text-on-dark)" }}>Assistant Savoir Utile</div>
                    <div className="text-[11px] flex items-center gap-1 truncate" style={{ color: "var(--gold-300)" }} data-testid="assistant-context-label">
                        <BookOpen className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{course ? `Cours : ${course.title}` : "Tous les cours de la plateforme"}</span>
                    </div>
                    {course && activity && (activity.track_title || activity.chapter) && (
                        <div className="text-[10px] truncate" style={{ color: "var(--text-on-dark-soft)" }} data-testid="assistant-activity-label">
                            {[activity.track_title && `En écoute : ${activity.track_title}`, activity.chapter && `En lecture : ${activity.chapter}`].filter(Boolean).join(" · ")}
                        </div>
                    )}
                </div>
                <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" data-testid="assistant-close-btn" aria-label="Fermer">
                    <X className="w-4 h-4" style={{ color: "var(--text-on-dark-soft)" }} />
                </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-messages">
                <AssistantMessage message={{ role: "assistant", text: course ? `Assalamou alaykoum ! Posez-moi votre question sur « ${course.title} ».` : "Assalamou alaykoum ! Comment puis-je vous aider dans votre apprentissage ?" }} intro />
                {teacherIntent && !answered && (
                    <div className="rounded-xl px-3.5 py-3 text-xs leading-relaxed" style={{ background: "rgba(201,164,88,0.12)", color: "var(--gold-100)", border: "1px solid var(--border-dark)" }} data-testid="assistant-teacher-intent-note">
                        Posez d'abord votre question ici : l'assistant répond souvent immédiatement. Si après plusieurs échanges vous ne comprenez toujours pas, il vous proposera lui-même de transmettre votre question au professeur de ce cours.
                    </div>
                )}
                {messages.map((m, i) => <AssistantMessage key={i} message={m} />)}
                {busy && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-on-dark-soft)" }} data-testid="assistant-thinking">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> L'assistant réfléchit…
                    </div>
                )}
                {error && <div className="text-xs" style={{ color: "#E8A0A0" }} data-testid="assistant-error">{error}</div>}
                {suggestTeacher && convId && !isAdmin && <EscalateBox conversationId={convId} />}
                {suggestTeacher && isAdmin && (
                    <div className="text-[11px] text-center" style={{ color: "var(--text-on-dark-soft)" }} data-testid="assistant-admin-note">
                        Mode admin : la transmission à un professeur est réservée aux élèves.
                    </div>
                )}
            </div>

            <form onSubmit={ask} className="p-3 flex items-end gap-2" style={{ borderTop: "1px solid var(--border-dark)" }}>
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) ask(e); }}
                    rows={1}
                    maxLength={1000}
                    placeholder="Votre question…"
                    className="flex-1 resize-none rounded-xl px-3.5 py-2.5 text-sm outline-none max-h-28"
                    style={{ background: "rgba(244,238,223,0.07)", color: "var(--text-on-dark)", border: "1px solid var(--border-dark)" }}
                    data-testid="assistant-input"
                />
                <button type="submit" disabled={busy || !input.trim()} className="btn-gold !px-3 !py-2.5 disabled:opacity-40" data-testid="assistant-send-btn" aria-label="Envoyer">
                    <Send className="w-4 h-4" />
                </button>
            </form>
        </motion.div>
    );
}
